import React, { useEffect } from 'react';
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Space, Typography, Radio } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getQuizInfo, updateQuiz } from '../../services/quiz';
import { getAllLessons } from '../../services/lesson';
import { getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import { getVideos } from '../../services/video';

const itemName = 'Đề ôn tập';
const { Text, Title } = Typography;

const EditQuiz = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const lessonId = Form.useWatch('lesson_id', form);

  const { data: itemInfo } = useQuery({ queryKey: ['quizInfo', id], queryFn: () => getQuizInfo(id) });
  const { data: lessonListRes } = useQuery({
    queryKey: ['lessonsForEditQuiz'],
    queryFn: () => getAllLessons(),
  });
  // Fetch and cache curriculums for 10 minutes
  const { data: curriculumListRes } = useQuery({
    queryKey: ['allCurriculums'],
    queryFn: () => getCurriculums({ page: 1, limit: 10000, status: [1] }),
    staleTime: 1000 * 60 * 10,
  });
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategoriesForQuizEdit'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const { data: videosRes } = useQuery({
    queryKey: ['videosForQuizEdit', lessonId],
    queryFn: () => getVideos({ page: 1, limit: 10000, status: [1], lesson_id: lessonId }),
    enabled: Number(lessonId || 0) > 0,
    staleTime: 1000 * 60 * 5,
  });
  const lessonOptions = React.useMemo(() => {
    const lessons = Array.isArray(lessonListRes?.data) ? lessonListRes.data : [];
    const currs = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const curTitle = currs.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const curCat = currs.reduce((acc, c) => { acc[c.id] = c.category_id || 0; return acc; }, {});
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toCatPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id; const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) { guard.add(cur); parts.unshift(titleMap[cur] || ''); cur = parentMap[cur] || 0; }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const opts = lessons.map((l) => {
      const cid = curCat[l.curriculum_id] || 0;
      const catPath = toCatPath(cid);
      const pTitle = curTitle[l.curriculum_id] || '';
      const lTitle = l.title || '';
      const label = `${catPath || '—'} - ${pTitle} - ${lTitle}`.trim();
      return { value: l.id, label, _cat: (catPath || '').toLowerCase(), _cur: (pTitle || '').toLowerCase(), _lesson: (lTitle || '').toLowerCase() };
    });
    opts.sort((a, b) => a._cat.localeCompare(b._cat) || a._cur.localeCompare(b._cur) || a._lesson.localeCompare(b._lesson));
    return opts;
  }, [lessonListRes, curriculumListRes, allCategoriesRes]);

  useEffect(() => {
    if (itemInfo) {
      form.setFieldsValue({
        lesson_id: itemInfo?.lesson_id,
        video_id: Number(itemInfo?.video_id || 0),
        title: itemInfo?.title,
        description: typeof itemInfo?.description === 'string' ? itemInfo.description : '',
        time_limit_minutes: itemInfo?.time_limit_minutes,
        passing_score: itemInfo?.passing_score,
        type: typeof itemInfo?.type === 'number' || typeof itemInfo?.type === 'string' ? Number(itemInfo.type) : 0,
        status: itemInfo?.status,
      });
    }
  }, [form, itemInfo]);

  const { mutate: mutateUpdate, isPending } = useMutation({
    mutationFn: ({ id, payload }) => updateQuiz(id, payload),
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Cập nhật ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (data) => {
    const payload = {
      lesson_id: data?.lesson_id,
      video_id: Number(data?.video_id || 0),
      title: data?.title,
      description: data?.description,
      time_limit_minutes: Number(data?.time_limit_minutes || 1),
      passing_score: Number(data?.passing_score ?? 70),
      type: Number(data?.type ?? 0),
      order: Number(data?.order || 1),
      status: data?.status,
    };
    mutateUpdate({ id, payload });
  };

  return (
    <Form name="edit-quiz" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Sửa {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="lesson_id"
            label={<Text>Bài học <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn bài học' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn bài học"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={lessonOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="video_id" label={<Text>Video (Không bắt buộc)</Text>} initialValue={0}>
            <Select
              showSearch
              allowClear
              disabled={!(Number(lessonId || 0) > 0)}
              placeholder="Chọn video"
              optionFilterProp="label"
              options={[
                { value: 0, label: 'Chọn video' },
                ...((Array.isArray(videosRes?.data) ? videosRes.data : []).map((v) => ({ value: v.id, label: v.title || `Video #${v.id}` })) || []),
              ]}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}
          >
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="description" label={<Text>Mô tả</Text>}>
            <Input.TextArea rows={6} placeholder="Nhập mô tả..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="time_limit_minutes" label={<Text>Thời gian làm (phút - Không bắt buộc)</Text>} initialValue={60}>
            <InputNumber min={1} precision={0} className="w-full" placeholder="Nhập thời gian (phút)" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="passing_score" label={<Text>Điểm đạt (%)</Text>} initialValue={70}>
            <InputNumber min={0} max={100} step={0.01} className="w-full" placeholder="Nhập điểm đạt (%)" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="type"
            label={<Text>Loại đề</Text>}
            initialValue={0}
          >
            <Radio.Group>
              <Radio value={0}>Cơ bản</Radio>
              <Radio value={1}>Nâng cao</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="order" label={<Text>Thứ tự</Text>} initialValue={1}>
            <InputNumber min={1} step={1} precision={0} className="w-full" placeholder="Nhập thứ tự (số nguyên dương)" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={<Text>Trạng thái <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default EditQuiz;
